// ============= Imports =============
import React, { useMemo, useState } from 'react';
import {
  Headers,
  LoadingSpinner,
  Spacer,
} from '@tw/tw-component-library';
import { Button } from '@/ui';
import { FilterPills } from '@/components/common/FilterPills';
import { CreateVisualPromptCard } from '@/components/visualPrompts/CreateVisualPromptCard';
import { CreateVisualPromptModal } from '@/components/visualPrompts/CreateVisualPromptModal';
import { DeleteVisualPromptModal } from '@/components/visualPrompts/DeleteVisualPromptModal';
import {
  VisualPromptCardGrid,
  VisualPromptWizard,
} from '@/components/visualPrompts';
import { CREATE_PROMPT_MESSAGES } from '@/constants/visualPrompts/createPromptMessages';
import { PROMPT_CATEGORY_FILTERS } from '@/constants/visualPrompts/promptCategories';
import { VISUAL_PROMPT_MESSAGES } from '@/constants/visualPrompts/messages';
import { useVisualPrompts } from '@/hooks/visualPrompts/useVisualPrompts';
import { useRunVisualPrompt } from '@/hooks/visualPrompts/useRunVisualPrompt';
import { AuthContextProps } from '@/contexts/AuthContext';
import type {
  VisualPromptAnswers,
  VisualPromptDefinition,
} from '@/types/visualPrompts/visualPrompt.types';
import { CardGroup, SectionTitle } from '../PromptLibrary.styles';
import { PlaceholderText } from '../../workspacePage/WorkSpacePage.styles';
import { ErrorRow, SearchInput, SectionDescription, Toolbar } from './styles';

// ============= Types =============
export interface PromptLibraryVisualPromptsSectionProps {
  workspaceId: string;
  workspaceTitle?: string;
  getAccessToken: AuthContextProps['getAccessToken'];
}

// ============= Component =============
export const PromptLibraryVisualPromptsSection: React.FC<
  PromptLibraryVisualPromptsSectionProps
> = ({ workspaceId, workspaceTitle, getAccessToken }) => {
  const {
    prompts,
    isLoading,
    error,
    filters,
    fetchPrompts,
    updateFilters,
    toggleFeatured,
  } = useVisualPrompts(workspaceId);
  const { runVisualPrompt, isRunning } = useRunVisualPrompt({
    workspaceId,
    getAccessToken,
  });

  const [activeDefinition, setActiveDefinition] =
    useState<VisualPromptDefinition | null>(null);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [promptToDelete, setPromptToDelete] =
    useState<VisualPromptDefinition | null>(null);

  const { customPrompts, builtInPrompts } = useMemo(() => {
    const custom: VisualPromptDefinition[] = [];
    const builtIn: VisualPromptDefinition[] = [];
    prompts.forEach((p) => {
      if (p.isCustom) {
        custom.push(p);
      } else {
        builtIn.push(p);
      }
    });
    return { customPrompts: custom, builtInPrompts: builtIn };
  }, [prompts]);

  const openWizard = (definition: VisualPromptDefinition) => {
    setActiveDefinition(definition);
    setWizardOpen(true);
  };

  const closeWizard = () => {
    setWizardOpen(false);
    setActiveDefinition(null);
  };

  const requestDelete = (definition: VisualPromptDefinition) => {
    setPromptToDelete(definition);
  };

  const handlePromptDeleted = () => {
    if (promptToDelete && activeDefinition?.id === promptToDelete.id) {
      closeWizard();
    }
    void fetchPrompts();
  };

  const handleRun = async (
    definition: VisualPromptDefinition,
    answers: VisualPromptAnswers,
  ) => {
    const ok = await runVisualPrompt(definition, answers);
    if (ok) {
      closeWizard();
    }
  };

  return (
    <>
      <SectionTitle>
        <Headers headerType="h2">{VISUAL_PROMPT_MESSAGES.sectionTitle}</Headers>
      </SectionTitle>
      <Spacer size={1} />
      <SectionDescription>
        {VISUAL_PROMPT_MESSAGES.sectionDescription}
      </SectionDescription>
      <SectionDescription style={{ fontSize: '0.85rem' }}>
        {VISUAL_PROMPT_MESSAGES.oneTimeUseNote}
      </SectionDescription>

      <Toolbar>
        <SearchInput
          type="search"
          placeholder={VISUAL_PROMPT_MESSAGES.searchPlaceholder}
          value={filters.search ?? ''}
          onChange={(e) => updateFilters({ search: e.target.value })}
        />
        <FilterPills
          label={VISUAL_PROMPT_MESSAGES.filterLabel}
          options={PROMPT_CATEGORY_FILTERS.map((c) => ({
            id: c.id,
            label: c.label,
          }))}
          currentFilter={filters.category ?? 'all'}
          onFilterChange={(category) => updateFilters({ category })}
        />
      </Toolbar>

      {isLoading ? (
        <LoadingSpinner />
      ) : error ? (
        <ErrorRow>
          <PlaceholderText>
            {VISUAL_PROMPT_MESSAGES.loadError}: {error.message}
          </PlaceholderText>
          <Button variant="secondary" onClick={() => void fetchPrompts()}>
            {VISUAL_PROMPT_MESSAGES.loadErrorRetry}
          </Button>
        </ErrorRow>
      ) : (
        <>
          <CreateVisualPromptCard onClick={() => setCreateModalOpen(true)} />
          <Spacer size={2} />
          {customPrompts.length > 0 && (
            <>
              <SectionDescription>
                <strong>{CREATE_PROMPT_MESSAGES.yourPrompts}</strong>
              </SectionDescription>
              <Spacer size={1} />
              <CardGroup>
                <VisualPromptCardGrid
                  prompts={customPrompts}
                  onSelectPrompt={openWizard}
                  onDeletePrompt={requestDelete}
                  onToggleFeatured={toggleFeatured}
                />
              </CardGroup>
              <Spacer size={2} />
            </>
          )}
          {builtInPrompts.length === 0 ? (
            <PlaceholderText>{VISUAL_PROMPT_MESSAGES.emptyList}</PlaceholderText>
          ) : (
            <CardGroup>
              <VisualPromptCardGrid
                prompts={builtInPrompts}
                onSelectPrompt={openWizard}
                onToggleFeatured={toggleFeatured}
              />
            </CardGroup>
          )}
        </>
      )}

      <CreateVisualPromptModal
        isOpen={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onSaved={() => void fetchPrompts()}
      />

      <DeleteVisualPromptModal
        definition={promptToDelete}
        onClose={() => setPromptToDelete(null)}
        onDeleted={handlePromptDeleted}
      />

      {wizardOpen && activeDefinition && (
        <VisualPromptWizard
          key={activeDefinition.id}
          isOpen={wizardOpen}
          definition={activeDefinition}
          onClose={closeWizard}
          onRun={handleRun}
          isRunning={isRunning}
          reviewContext={{ workspaceName: workspaceTitle }}
        />
      )}
    </>
  );
};
